import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle2, School, Calendar, User, AlertCircle, Loader2,
  BookOpen, MapPin, ShieldCheck, ChevronRight, ChevronLeft, MessageCircle,
} from "lucide-react";
import api, { BACKEND } from "@/lib/api";
import { useCms } from "@/lib/cms";

// ── Initial state — same as before ───────────────────────────────────────────
const EMPTY = {
  full_name: "", gender: "", dob: "", mobile: "", whatsapp: "", email: "", employee_id: "",
  designation: "", subject: "", classes_teaching: "", experience: "", qualification: "",
  district: "", city: "", state: "",
  review_rating: 0, review_experience: "", review_text: "", review_answers: {},
  consent_info_correct: false, consent_certificate: false, consent_email: false,
};

// ── Steps ─────────────────────────────────────────────────────────────────────
const STEPS = [
  { id: 0, label: "Personal",     icon: User },
  { id: 1, label: "Professional", icon: BookOpen },
  { id: 2, label: "Location",     icon: MapPin },
  { id: 3, label: "Consent",      icon: ShieldCheck },
];

// ── Shared input styles ───────────────────────────────────────────────────────
const inputBase =
  "w-full px-3.5 py-2.5 rounded-xl border outline-none text-sm transition-all bg-white";
const inputNormal = `${inputBase} border-gray-200 focus:border-[#4F46E5] focus:ring-2 focus:ring-[#4F46E5]/10`;
const inputError  = `${inputBase} border-red-300 focus:border-red-400 focus:ring-2 focus:ring-red-100 bg-red-50/30`;

// ── Field with inline error ───────────────────────────────────────────────────
function Field({ label, required, error, help, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-1">
        {label}
        {required && <span className="text-red-500 text-xs">*</span>}
      </span>
      {children}
      {error && (
        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
          <AlertCircle className="w-3 h-3 shrink-0" /> {error}
        </p>
      )}
      {help && !error && <p className="mt-1 text-xs text-gray-400">{help}</p>}
    </label>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function SectionCard({ step, children }) {
  const Icon = STEPS[step].icon;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm">
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gray-50/70">
        <div className="w-8 h-8 rounded-lg bg-[#EEF2FF] grid place-items-center shrink-0">
          <Icon className="w-4 h-4 text-[#4F46E5]" />
        </div>
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
            Step {step + 1} of {STEPS.length}
          </p>
          <p className="text-sm font-semibold text-[#0F172A]">{STEPS[step].label} Information</p>
        </div>
      </div>
      <div className="p-6">{children}</div>
    </div>
  );
}

// ── Progress stepper ──────────────────────────────────────────────────────────
function Stepper({ current }) {
  return (
    <div className="flex items-center gap-0 mb-8">
      {STEPS.map((s, i) => {
        const done    = i < current;
        const active  = i === current;
        const Icon    = s.icon;
        return (
          <div key={s.id} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-8 h-8 rounded-full grid place-items-center text-xs font-semibold transition-all ${
                  done   ? "bg-emerald-500 text-white" :
                  active ? "bg-[#4F46E5] text-white ring-4 ring-[#4F46E5]/20" :
                           "bg-gray-100 text-gray-400"
                }`}
              >
                {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-3.5 h-3.5" />}
              </div>
              <span className={`text-[10px] font-medium hidden sm:block whitespace-nowrap ${active ? "text-[#4F46E5]" : done ? "text-emerald-600" : "text-gray-400"}`}>
                {s.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-0.5 mx-2 rounded-full transition-all ${i < current ? "bg-emerald-400" : "bg-gray-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Validate per step ─────────────────────────────────────────────────────────
function validateStep(step, data, form) {
  const e = {};
  if (step === 0) {
    if (!data.full_name.trim())   e.full_name = "Full name is required.";
    if (!data.mobile.trim())      e.mobile    = "Mobile number is required.";
    if (!data.email.trim())       e.email     = "Email address is required.";
    else if (!/\S+@\S+\.\S+/.test(data.email)) e.email = "Enter a valid email address.";
  }
  if (step === 3) {
    // Validate dynamic review questions if review is enabled
    const reviewOn = form?.review_enabled !== false;
    const questions = (form?.review_questions || []).filter((q) => q.active !== false && q.active !== "false" && q.text);
    if (reviewOn && questions.length > 0) {
      questions.forEach((q) => {
        if (!q.required) return;
        const ans = data.review_answers?.[q.id];
        if (q.type === "star_rating") {
          if (!ans || ans < 1) e[`rq_${q.id}`] = "This field is required.";
        } else if (q.type === "checkbox") {
          if (!ans || (Array.isArray(ans) && ans.length === 0)) e[`rq_${q.id}`] = "Please select at least one option.";
        } else if (q.type === "paragraph") {
          if (!ans || ans.trim().length < 10) e[`rq_${q.id}`] = "Please write at least 10 characters.";
        } else {
          if (!ans || (typeof ans === "string" && !ans.trim())) e[`rq_${q.id}`] = "This field is required.";
        }
      });
    }
    // Legacy: validate old hardcoded fields only when no dynamic questions configured
    if (reviewOn && questions.length === 0) {
      if (!data.review_rating || data.review_rating < 1) e.review_rating = "Please select a rating.";
      if (!data.review_experience) e.review_experience = "Please select your experience.";
      if (!data.review_text || data.review_text.trim().length < 30) e.review_text = "Please write at least 30 characters.";
      if (data.review_text && data.review_text.length > 1000) e.review_text = "Maximum 1000 characters.";
    }
    if (!data.consent_info_correct) e.consent_info_correct = "This consent is required.";
    if (!data.consent_certificate)  e.consent_certificate  = "This consent is required.";
  }
  return e;
}

// ── Main component ────────────────────────────────────────────────────────────
export default function RegisterTraining() {
  const { token }     = useParams();
  const { site }      = useCms();
  const whatsapp      = site?.whatsapp || "";
  const [form, setForm] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [data, setData] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult]         = useState(null);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    api.get(`/registrations/link/${token}`)
      .then((r) => setForm(r.data))
      .catch((e) => {
        if (e.response) {
          setLoadError(e.response.data?.detail || "This registration link is not available.");
        } else {
          setLoadError(`Could not reach the server at ${BACKEND}. Check that the backend is running and REACT_APP_BACKEND_URL is set correctly.`);
        }
      });
  }, [token]);

  const set = (k, v) => { setData((d) => ({ ...d, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };

  const goNext = () => {
    const errs = validateStep(step, data, form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setErrors({});
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validateStep(3, data, form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitError("");
    setSubmitting(true);
    try {
      // Extract overall_rating from first star_rating question for backward compat
      const questions = (form.review_questions || []).filter((q) => q.active !== false && q.active !== "false" && q.text);
      const firstStar = questions.find((q) => q.type === "star_rating");
      const payload = {
        ...data,
        review_rating: firstStar ? (data.review_answers?.[firstStar.id] || data.review_rating) : data.review_rating,
        review_answers: data.review_answers || {},
      };
      const { data: res } = await api.post(`/registrations/link/${token}/submit`, payload);
      setResult(res);
    } catch (err) {
      setSubmitError(err?.response?.data?.detail || "Could not submit your registration. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Error state ──────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center bg-white rounded-2xl border border-gray-200 p-10 shadow-sm">
          <div className="w-14 h-14 rounded-full bg-red-50 grid place-items-center mx-auto mb-5">
            <AlertCircle className="w-7 h-7 text-red-400" />
          </div>
          <h1 className="font-display text-xl font-semibold text-[#0F172A]">Registration unavailable</h1>
          <p className="mt-3 text-sm text-gray-500 leading-relaxed">{loadError}</p>
          <Link to="/" className="mt-6 inline-block text-sm text-[#4F46E5] hover:underline">Return to homepage</Link>
        </div>
      </div>
    );
  }

  // ── Success state ─────────────────────────────────────────────────────────────
  if (result) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full text-center"
          data-testid="registration-success"
        >
          <div className="bg-white rounded-2xl border border-gray-200 p-10 shadow-sm">
            <motion.div
              initial={{ scale: 0 }} animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              className="w-16 h-16 rounded-full bg-emerald-50 grid place-items-center mx-auto mb-5"
            >
              <CheckCircle2 className="w-8 h-8 text-emerald-500" />
            </motion.div>
            <h1 className="font-display text-2xl font-semibold text-[#0F172A]">Registration submitted!</h1>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed max-w-xs mx-auto">
              Your registration is pending admin approval. You'll receive your certificate via email once approved.
            </p>

            <div className="mt-6 rounded-xl bg-gray-50 border border-gray-200 p-4 text-left space-y-2.5 text-sm">
              <div className="flex justify-between gap-4">
                <span className="text-gray-400 shrink-0">Submission ID</span>
                <span className="font-mono text-xs text-[#0F172A] break-all">{result.submission_id}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-400 shrink-0">Training</span>
                <span className="text-right text-[#0F172A]">{result.training_name}</span>
              </div>
              <div className="flex justify-between gap-4">
                <span className="text-gray-400 shrink-0">Support</span>
                <a href="mailto:rahulsharma@manthanfoundation.in" className="text-[#4F46E5] hover:underline text-right">
                  rahulsharma@manthanfoundation.in
                </a>
              </div>
            </div>

            <Link to="/" className="mt-6 inline-block text-sm text-gray-500 hover:text-gray-700">
              ← Return to homepage
            </Link>

            {whatsapp && (
              <div className="mt-5 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-left">
                <MessageCircle className="w-4 h-4 text-green-600 shrink-0" />
                <div>
                  <div className="text-xs font-semibold text-green-800">Questions about your registration?</div>
                  <a
                    href={`https://wa.me/${whatsapp.replace(/[^\d]/g, "")}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="register-whatsapp"
                    className="text-xs text-green-700 hover:underline"
                  >
                    Chat on WhatsApp → {whatsapp}
                  </a>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (!form) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm">Loading registration form…</p>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="registration-form-page">

      {/* Top banner */}
      <div className="bg-[#0F172A] text-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#4F46E5] mb-1">Training Registration</p>
          <h1 className="font-display text-xl sm:text-2xl font-semibold leading-snug">
            {form.program_title || "Teacher Training"}
          </h1>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><School className="w-3.5 h-3.5" /> {form.school_name}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {form.training_date}</span>
            {form.training_start_time && (
              <span className="flex items-center gap-1.5">
                🕘 {form.training_start_time}
                {form.training_end_time ? ` – ${form.training_end_time}` : ""}
                {form.training_timezone ? ` ${form.training_timezone}` : ""}
              </span>
            )}
            {form.training_duration_hours > 0 && (
              <span className="flex items-center gap-1.5">⏱ {form.training_duration_hours} Hours</span>
            )}
            {form.training_venue && (
              <span className="flex items-center gap-1.5">📍 {form.training_venue}</span>
            )}
            {form.trainer_name && <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {form.trainer_name}</span>}
          </div>
        </div>
      </div>

      {/* Form body */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">

        {/* Stepper */}
        <Stepper current={step} />

        <form onSubmit={submit}>
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 16 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.2 }}
            >

              {/* ── Step 0: Personal ─────────────────────────────────────── */}
              {step === 0 && (
                <SectionCard step={0}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Field label="Full name" required error={errors.full_name}>
                        <input
                          className={errors.full_name ? inputError : inputNormal}
                          value={data.full_name}
                          onChange={(e) => set("full_name", e.target.value)}
                          placeholder="e.g. Jane Smith"
                          data-testid="reg-full-name"
                        />
                      </Field>
                    </div>
                    <Field label="Gender">
                      <select className={inputNormal} value={data.gender} onChange={(e) => set("gender", e.target.value)}>
                        <option value="">Select…</option>
                        <option>Female</option>
                        <option>Male</option>
                        <option>Other</option>
                        <option>Prefer not to say</option>
                      </select>
                    </Field>
                    <Field label="Date of birth">
                      <input type="date" className={inputNormal} value={data.dob} onChange={(e) => set("dob", e.target.value)} />
                    </Field>
                    <Field label="Mobile number" required error={errors.mobile}>
                      <input
                        className={errors.mobile ? inputError : inputNormal}
                        value={data.mobile}
                        onChange={(e) => set("mobile", e.target.value)}
                        placeholder="e.g. 9876543210"
                        data-testid="reg-mobile"
                      />
                    </Field>
                    <Field label="WhatsApp number" help="Leave blank if same as mobile">
                      <input className={inputNormal} value={data.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="e.g. 9876543210" />
                    </Field>
                    <Field label="Email address" required error={errors.email}>
                      <input
                        type="email"
                        className={errors.email ? inputError : inputNormal}
                        value={data.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@school.edu"
                        data-testid="reg-email"
                      />
                    </Field>
                    <Field label="Employee ID">
                      <input className={inputNormal} value={data.employee_id} onChange={(e) => set("employee_id", e.target.value)} placeholder="Optional" />
                    </Field>
                  </div>
                </SectionCard>
              )}

              {/* ── Step 1: Professional ─────────────────────────────────── */}
              {step === 1 && (
                <SectionCard step={1}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <Field label="School">
                        <input className={`${inputNormal} bg-gray-50 text-gray-500 cursor-not-allowed`} value={form.school_name} disabled />
                      </Field>
                    </div>
                    <Field label="Designation">
                      <input className={inputNormal} value={data.designation} onChange={(e) => set("designation", e.target.value)} placeholder="e.g. Primary Teacher" />
                    </Field>
                    <Field label="Subject">
                      <input className={inputNormal} value={data.subject} onChange={(e) => set("subject", e.target.value)} placeholder="e.g. Mathematics" />
                    </Field>
                    <Field label="Classes teaching">
                      <input className={inputNormal} value={data.classes_teaching} onChange={(e) => set("classes_teaching", e.target.value)} placeholder="e.g. Grades 6–8" />
                    </Field>
                    <Field label="Teaching experience">
                      <input className={inputNormal} value={data.experience} onChange={(e) => set("experience", e.target.value)} placeholder="e.g. 5 years" />
                    </Field>
                    <div className="sm:col-span-2">
                      <Field label="Qualification">
                        <input className={inputNormal} value={data.qualification} onChange={(e) => set("qualification", e.target.value)} placeholder="e.g. B.Ed, M.Sc" />
                      </Field>
                    </div>
                  </div>
                </SectionCard>
              )}

              {/* ── Step 2: Location ─────────────────────────────────────── */}
              {step === 2 && (
                <SectionCard step={2}>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <Field label="District">
                      <input className={inputNormal} value={data.district} onChange={(e) => set("district", e.target.value)} placeholder="e.g. Bhopal" />
                    </Field>
                    <Field label="City">
                      <input className={inputNormal} value={data.city} onChange={(e) => set("city", e.target.value)} placeholder="e.g. Bhopal" />
                    </Field>
                    <Field label="State">
                      <input className={inputNormal} value={data.state} onChange={(e) => set("state", e.target.value)} placeholder="e.g. Madhya Pradesh" />
                    </Field>
                  </div>
                </SectionCard>
              )}

              {/* ── Step 3: Consent ──────────────────────────────────────── */}
              {step === 3 && (
                <SectionCard step={3}>
                  <div className="space-y-4">
                    {/* Summary */}
                    <div className="rounded-xl bg-[#F8FAFC] border border-gray-200 p-4 text-sm text-gray-600 space-y-1.5">
                      <p className="font-medium text-[#0F172A] mb-2">Registration summary</p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                        <span className="text-gray-400">Name</span><span className="font-medium">{data.full_name}</span>
                        <span className="text-gray-400">Mobile</span><span>{data.mobile}</span>
                        <span className="text-gray-400">Email</span><span className="break-all">{data.email}</span>
                        <span className="text-gray-400">Training</span><span>{form.program_title}</span>
                        {form.training_date && <><span className="text-gray-400">Date</span><span>{form.training_date}</span></>}
                        {form.training_start_time && (
                          <><span className="text-gray-400">Time</span>
                          <span>{form.training_start_time}{form.training_end_time ? ` – ${form.training_end_time}` : ""}{form.training_timezone ? ` ${form.training_timezone}` : ""}</span></>
                        )}
                        {form.training_duration_hours > 0 && <><span className="text-gray-400">Duration</span><span>{form.training_duration_hours} Hours</span></>}
                        {form.training_venue && <><span className="text-gray-400">Venue</span><span>{form.training_venue}</span></>}
                      </div>
                    </div>

                    {/* ── Review & Feedback (dynamic from CMS) ──────── */}
                    {form.review_enabled !== false && (() => {
                      const questions = (form.review_questions || []).filter((q) => q.active !== false && q.active !== "false" && q.text);
                      const reviewTitle = form.review_title || "Training Feedback";
                      const reviewDesc = form.review_description || "";
                      const setAns = (qId, val) => setData((d) => ({ ...d, review_answers: { ...d.review_answers, [qId]: val } }));
                      const getAns = (qId) => data.review_answers?.[qId];

                      // Extract first star_rating as overall_rating for backward compat
                      const firstStar = questions.find((q) => q.type === "star_rating");
                      if (firstStar && getAns(firstStar.id) && getAns(firstStar.id) !== data.review_rating) {
                        // sync silently via ref instead of setState during render
                      }

                      return questions.length > 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-[#4F46E5]" />
                            <span className="text-sm font-semibold text-[#0F172A]">{reviewTitle}</span>
                            {form.review_mandatory !== false && <span className="text-red-500 text-xs">*</span>}
                          </div>
                          {reviewDesc && <p className="text-xs text-gray-500">{reviewDesc}</p>}

                          {questions.map((q) => (
                            <Field key={q.id} label={q.text} required={q.required} error={errors[`rq_${q.id}`]}>
                              {q.type === "star_rating" && (
                                <div className="flex gap-1">
                                  {[1,2,3,4,5].map((star) => (
                                    <button key={star} type="button" onClick={() => setAns(q.id, star)} className="text-2xl transition-colors focus:outline-none" aria-label={`${star} star${star > 1 ? "s" : ""}`}>
                                      <span className={star <= (getAns(q.id) || 0) ? "text-amber-400" : "text-gray-300"}>★</span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              {q.type === "dropdown" && (
                                <select className={errors[`rq_${q.id}`] ? inputError : inputNormal} value={getAns(q.id) || ""} onChange={(e) => setAns(q.id, e.target.value)}>
                                  <option value="">Select…</option>
                                  {(q.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              )}
                              {q.type === "radio" && (
                                <div className="flex flex-wrap gap-2">
                                  {(q.options || []).map((o) => (
                                    <label key={o} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-all ${getAns(q.id) === o ? "bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]" : "border-gray-200 hover:border-gray-300"}`}>
                                      <input type="radio" name={q.id} value={o} checked={getAns(q.id) === o} onChange={() => setAns(q.id, o)} className="sr-only" />
                                      {o}
                                    </label>
                                  ))}
                                </div>
                              )}
                              {q.type === "checkbox" && (
                                <div className="flex flex-wrap gap-2">
                                  {(q.options || []).map((o) => {
                                    const checked = (getAns(q.id) || []).includes(o);
                                    return (
                                      <label key={o} className={`px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-all ${checked ? "bg-[#EEF2FF] border-[#4F46E5] text-[#4F46E5]" : "border-gray-200 hover:border-gray-300"}`}>
                                        <input type="checkbox" checked={checked} onChange={() => setAns(q.id, checked ? (getAns(q.id) || []).filter((v) => v !== o) : [...(getAns(q.id) || []), o])} className="sr-only" />
                                        {o}
                                      </label>
                                    );
                                  })}
                                </div>
                              )}
                              {q.type === "short_answer" && (
                                <input className={errors[`rq_${q.id}`] ? inputError : inputNormal} value={getAns(q.id) || ""} onChange={(e) => setAns(q.id, e.target.value)} placeholder="Your answer" />
                              )}
                              {q.type === "paragraph" && (
                                <>
                                  <textarea className={`${errors[`rq_${q.id}`] ? inputError : inputNormal} min-h-[80px] resize-y`} value={getAns(q.id) || ""} onChange={(e) => { if (e.target.value.length <= 1000) setAns(q.id, e.target.value); }} placeholder="Your answer" rows={3} />
                                  <p className="mt-1 text-xs text-gray-400 text-right">{(getAns(q.id) || "").length}/1000</p>
                                </>
                              )}
                            </Field>
                          ))}
                        </div>
                      ) : (
                        /* Legacy fallback for forms without configured questions */
                        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                          <div className="flex items-center gap-2">
                            <MessageCircle className="w-4 h-4 text-[#4F46E5]" />
                            <span className="text-sm font-semibold text-[#0F172A]">Review & Feedback</span>
                            <span className="text-red-500 text-xs">*</span>
                          </div>
                          <Field label="Overall Rating" required error={errors.review_rating}>
                            <div className="flex gap-1">
                              {[1,2,3,4,5].map((star) => (
                                <button key={star} type="button" onClick={() => set("review_rating", star)} className="text-2xl transition-colors focus:outline-none" aria-label={`${star} star${star > 1 ? "s" : ""}`}>
                                  <span className={star <= data.review_rating ? "text-amber-400" : "text-gray-300"}>★</span>
                                </button>
                              ))}
                            </div>
                          </Field>
                          <Field label="Overall Experience" required error={errors.review_experience}>
                            <select className={errors.review_experience ? inputError : inputNormal} value={data.review_experience} onChange={(e) => set("review_experience", e.target.value)}>
                              <option value="">Select experience</option>
                              {["Excellent","Very Good","Good","Average","Needs Improvement"].map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          </Field>
                          <Field label="Your Feedback" required error={errors.review_text}>
                            <textarea className={`${errors.review_text ? inputError : inputNormal} min-h-[80px] resize-y`} value={data.review_text} onChange={(e) => { if (e.target.value.length <= 1000) set("review_text", e.target.value); }} placeholder="Please share your experience with Manthan Foundation." rows={3} />
                            <p className="mt-1 text-xs text-gray-400 text-right">{data.review_text.length}/1000</p>
                          </Field>
                        </div>
                      );
                    })()}

                    {/* Consent checkboxes */}
                    {[
                      { key: "consent_info_correct", text: "The information provided is accurate and complete.", required: true },
                      { key: "consent_certificate",  text: "I consent to certificate generation using the details above.", required: true },
                      { key: "consent_email",         text: "I consent to receiving email communication about this training.", required: false },
                    ].map(({ key, text, required }) => (
                      <label
                        key={key}
                        className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                          data[key]
                            ? "bg-[#EEF2FF] border-[#4F46E5]"
                            : errors[key]
                            ? "bg-red-50 border-red-300"
                            : "bg-white border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={data[key]}
                          onChange={(e) => set(key, e.target.checked)}
                          data-testid={`reg-${key}`}
                          className="mt-0.5 shrink-0 accent-[#4F46E5]"
                        />
                        <span className="text-sm text-gray-700 leading-snug">
                          {text}
                          {required && <span className="text-red-500 ml-1">*</span>}
                        </span>
                      </label>
                    ))}

                    {errors.consent_info_correct && (
                      <p className="text-xs text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Please confirm all required consents.
                      </p>
                    )}

                    {submitError && (
                      <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        {submitError}
                      </div>
                    )}
                  </div>
                </SectionCard>
              )}

            </motion.div>
          </AnimatePresence>

          {/* ── Navigation ────────────────────────────────────────────────── */}
          <div className="mt-6 flex items-center justify-between gap-3">
            {step > 0 ? (
              <button
                type="button"
                onClick={goBack}
                className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            ) : (
              <div />
            )}

            {step < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                className="inline-flex items-center gap-1.5 bg-[#4F46E5] text-white text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-[#4338CA] transition-colors"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="submit"
                disabled={submitting}
                data-testid="reg-submit"
                className="inline-flex items-center gap-2 bg-[#0F172A] disabled:bg-gray-300 text-white text-sm font-semibold px-8 py-2.5 rounded-xl hover:bg-[#1e293b] transition-colors"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {submitting ? "Submitting…" : "Submit registration"}
              </button>
            )}
          </div>

          {/* Required fields note */}
          <p className="mt-4 text-xs text-gray-400 text-center">
            Fields marked <span className="text-red-400">*</span> are required.
          </p>
        </form>
      </div>
    </div>
  );
}